import { getSupabaseClient, isSupabaseConfigured } from './supabase'

// Database service functions for Elements app
export const database = {
  // User profile and settings
  async getUserProfile(userId) {
    if (!isSupabaseConfigured()) return null
    
    try {
      const client = getSupabaseClient()
      const { data, error } = await client
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .single()
      
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error('Error fetching user profile:', error)
        return null
      }
      
      return data
    } catch (err) {
      console.error('Database connection error in getUserProfile:', err)
      throw err // Re-throw to be caught by health check
    }
  },

  async updateUserProfile(userId, profile) {
    if (!isSupabaseConfigured()) return null
    
    try {
      const client = getSupabaseClient()
      
      // First, try to get existing profile
      const { data: existingProfile } = await client
        .from('user_profiles')
        .select('id')
        .eq('user_id', userId)
        .single()
      
      if (existingProfile) {
        // Update existing profile
        const { data, error } = await client
          .from('user_profiles')
          .update({
            ...profile,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
          .select()
          .single()
        
        if (error) {
          console.error('Error updating existing user profile:', error)
          return null
        }
        
        return data
      } else {
        // Insert new profile
        const { data, error } = await client
          .from('user_profiles')
          .insert({
            user_id: userId,
            ...profile,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single()
        
        if (error) {
          console.error('Error creating new user profile:', error)
          return null
        }
        
        return data
      }
    } catch (err) {
      console.error('Database connection error in updateUserProfile:', err)
      throw err
    }
  },

  // Elements entries
  async getEntries(userId, limit = 50) {
    if (!isSupabaseConfigured()) return []
    
    try {
      const client = getSupabaseClient()
      const { data, error } = await client
        .from('entries')
        .select('*')
        .eq('user_id', userId)
        .order('date', { ascending: false })
        .limit(limit)
      
      if (error) {
        console.error('Error fetching entries:', error)
        return []
      }
      
      return data || []
    } catch (err) {
      console.error('Database connection error in getEntries:', err)
      throw err
    }
  },

  async getEntryByDate(userId, date) {
    if (!isSupabaseConfigured()) return null
    
    try {
      const client = getSupabaseClient()
      const { data, error } = await client
        .from('entries')
        .select('*')
        .eq('user_id', userId)
        .eq('date', date)
        .single()
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching entry:', error)
        return null
      }
      
      return data
    } catch (err) {
      console.error('Database connection error in getEntryByDate:', err)
      throw err
    }
  },

  async saveEntry(userId, entry) {
    if (!isSupabaseConfigured()) return null
    
    try {
      const client = getSupabaseClient()
      
      // First, try to get existing entry
      const { data: existingEntry } = await client
        .from('entries')
        .select('id')
        .eq('user_id', userId)
        .eq('date', entry.date)
        .single()
      
      if (existingEntry) {
        // Update existing entry
        const { data, error } = await client
          .from('entries')
          .update({
            values: entry.values,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
          .eq('date', entry.date)
          .select()
          .single()
        
        if (error) {
          console.error('Error updating existing entry:', error)
          return null
        }
        
        return data
      } else {
        // Insert new entry
        const { data, error } = await client
          .from('entries')
          .insert({
            user_id: userId,
            date: entry.date,
            values: entry.values,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single()
        
        if (error) {
          console.error('Error creating new entry:', error)
          return null
        }
        
        return data
      }
    } catch (err) {
      console.error('Database connection error in saveEntry:', err)
      throw err
    }
  },

  async deleteEntry(userId, entryId) {
    if (!isSupabaseConfigured()) return false
    
    try {
      const client = getSupabaseClient()
      const { error } = await client
        .from('entries')
        .delete()
        .eq('user_id', userId)
        .eq('id', entryId)
      
      if (error) {
        console.error('Error deleting entry:', error)
        return false
      }
      
      return true
    } catch (err) {
      console.error('Database connection error in deleteEntry:', err)
      throw err
    }
  }
}

// Database schema (for reference and setup)
export const DATABASE_SCHEMA = {
  user_profiles: `
    CREATE TABLE user_profiles (
      id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
      user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE,
      onboarding_done BOOLEAN DEFAULT false,
      utopia JSONB DEFAULT '{}',
      theme TEXT DEFAULT 'Stoicism',
      streak_goal BOOLEAN DEFAULT false,
      cycle_tracking JSONB DEFAULT NULL,
      rating_tracking JSONB DEFAULT NULL,
      created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
      updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
      UNIQUE(user_id)
    );
  `,
  
  // Migration to add cycle_tracking column (run this if the table already exists)
  migration_add_cycle_tracking: `
    ALTER TABLE user_profiles 
    ADD COLUMN IF NOT EXISTS cycle_tracking JSONB DEFAULT NULL;
  `,
  
  // Migration to add rating_tracking column (run this if the table already exists)
  migration_add_rating_tracking: `
    ALTER TABLE user_profiles 
    ADD COLUMN IF NOT EXISTS rating_tracking JSONB DEFAULT NULL;
  `,
  
  entries: `
    CREATE TABLE entries (
      id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
      user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE,
      date DATE NOT NULL,
      values JSONB NOT NULL DEFAULT '{}',
      created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
      updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
      UNIQUE(user_id, date)
    );
  `,
  
  // Row Level Security policies
  policies: `
    -- Enable RLS
    ALTER TABLE user_profiles ENABLE ROW LEVEL SECURITY;
    ALTER TABLE entries ENABLE ROW LEVEL SECURITY;
    
    -- User profiles policies
    CREATE POLICY "Users can view own profile" ON user_profiles
      FOR SELECT USING (auth.uid() = user_id);
    
    CREATE POLICY "Users can update own profile" ON user_profiles
      FOR ALL USING (auth.uid() = user_id);
    
    -- Entries policies
    CREATE POLICY "Users can view own entries" ON entries
      FOR SELECT USING (auth.uid() = user_id);
    
    CREATE POLICY "Users can insert own entries" ON entries
      FOR INSERT WITH CHECK (auth.uid() = user_id);
    
    CREATE POLICY "Users can update own entries" ON entries
      FOR UPDATE USING (auth.uid() = user_id);
    
    CREATE POLICY "Users can delete own entries" ON entries
      FOR DELETE USING (auth.uid() = user_id);
  `
}
